import type { ECard } from '@graphql/generated-base';
import { useChatGPTTextCompletionQuery } from '../../../apps/admin/postOffice/PostOffice.graphql.generated';
import { AiModelVersion } from '@graphql/generated-base';
import { useState } from 'react';
import type { GiftWrapType } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useEventLocale } from '@localization/context/LocaleContext';
import { sanitizeLocales, tryParseLocaleRegion } from '@localization/safeIntl';
import { DEFAULT_LANGUAGE_BASE } from '@shared/core/i18n/i18n.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addError } from '@shared/utils/logger';

interface GiftNoteParams {
  prompt: string;
  fallback: string;
  skip?: boolean;
  onCompleted: (note: string, prompt: string) => void;
}

const FORBIDDEN_PHRASES = new Set([
  'husband',
  'wife',
  'bride',
  'groom',
  'mr',
  'mrs',
  'sir',
  'madam',
  'lady',
  'father',
  'mother',
  'dad',
  'mom',
  'man',
  'maid',
  'girl',
  'woman',
  'boy',
]);

const FORBIDDEN_PHRASE_EXAMPLES = Array.from(FORBIDDEN_PHRASES).join(', ');

const isEnglishLanguage = (language?: string | null) => {
  if (!language) return true;
  return language.toLowerCase().split('-')[0] === DEFAULT_LANGUAGE_BASE;
};

export const containsForbiddenPhrase = (text: string, language?: string | null) => {
  if (!isEnglishLanguage(language)) return false;

  const words = text.toLowerCase().match(/\b\w+\b/g) || [];
  return words.some(word => FORBIDDEN_PHRASES.has(word));
};

export const useGenerateGiftNote = ({ prompt, fallback, skip, onCompleted }: GiftNoteParams) => {
  const { language } = useEventLocale();
  const { loading: isEventInfoLoading } = useEventInfo();
  const [originalNote, setOriginalNote] = useState('');
  const { loading: isCompletionLoading } = useChatGPTTextCompletionQuery({
    variables: {
      prompt,
      modelVersion: AiModelVersion.gpt35turboInstruct,
    },
    skip: skip || isEventInfoLoading || !!originalNote,
    batchMode: 'off',
    onCompleted(data) {
      // ChatGPT often returns noise characters (quotes, dashes, whitespace) at the beginning.
      // Trim any leading characters that aren't a letter or number in any script, then trim trailing spaces.
      const note = data.textCompletion.replace(/^[^\p{L}\p{N}]+/u, '').trimEnd();
      if (containsForbiddenPhrase(note, language)) {
        addError('Generated note contains forbidden phrases, using fallback instead.', { action: 'giftNoteForbiddenPhrase', note, prompt, language });
        setOriginalNote(fallback);
        onCompleted(fallback, prompt);
        return;
      }

      setOriginalNote(note);
      onCompleted(note, prompt);
    },
    onError(error) {
      addError(error, { action: 'giftNoteGenerationError', prompt, language });
      setOriginalNote(fallback);
      onCompleted(fallback, prompt);
    },
  });

  return { originalNote, isGeneratedMessageLoading: isCompletionLoading || (!skip && isEventInfoLoading) };
};

export const prepareECardPayload = (payload: { price: number; draftId: string; theme: string; imageURL?: string; offerType: GiftWrapType }): ECard => {
  // eCard processing requires price in minor units (cents with no decimals)
  const priceInMajorUnits = payload.price * 100;
  return {
    price: priceInMajorUnits,
    emailDraftId: payload.draftId,
    quantity: 1,
    type: 'GIFT_WRAP_BASIC',
    details: [{ key: 'theme', value: payload.theme }],
    imageURL: payload.imageURL,
    offerType: payload.offerType,
  };
};

// Workaround: price estimates need an eCard payload to calculate taxes before a
// draft exists, while the sample draft ID and theme are ignored by the calculation.
export const prepareSampleECardPayload = (price: number, offerType: GiftWrapType): ECard =>
  prepareECardPayload({
    price,
    draftId: 'sample-ecard',
    theme: 'sample-ecard',
    offerType,
  });

export const parseGiftWrapPayload = (payload: string) => {
  try {
    return JSON.parse(payload) as { message: string; themeId: string; image: string; store?: string; title: string; giftLink: string };
  } catch {
    return null;
  }
};

const countryNameByRegion = new Map<string, string | undefined>();

export const countryNameFromLocale = (locale?: string | null): string | undefined => {
  const region = tryParseLocaleRegion(locale);
  if (!region) return undefined;
  if (countryNameByRegion.has(region)) return countryNameByRegion.get(region);

  let countryName: string | undefined;
  try {
    // eslint-disable-next-line compat/compat -- missing on old Safari; the catch drops the country and the prompt omits its culture line
    countryName = new Intl.DisplayNames(sanitizeLocales(['en']), { type: 'region' }).of(region) ?? undefined;
  } catch {
    countryName = undefined;
  }
  countryNameByRegion.set(region, countryName);
  return countryName;
};

export const resolveGiftWrapCountry = ({ venueCountry, locale }: { venueCountry?: string | null; locale?: string | null }): string | undefined => {
  const fromVenue = venueCountry?.trim();
  if (fromVenue) return fromVenue;
  return countryNameFromLocale(locale);
};

export const useGiftNoteLocale = (): { language: string; country?: string } => {
  const { language } = useEventLocale();
  const { eventInfo } = useEventInfo();
  return { language, country: resolveGiftWrapCountry({ venueCountry: eventInfo?.locationInfo?.country, locale: eventInfo?.locale }) };
};

const buildCultureLine = (country: string | undefined, noteDescription: string) =>
  country
    ? `Culture: write the way a guest in ${country} would write a ${noteDescription}. Match local greeting customs and register, not US defaults. Same language family is not the same culture (UK ≠ Australia ≠ US).`
    : '';

const INCLUSIVE_LANGUAGE_LINES = `Language: inclusive—avoid gendered roles or titles (e.g., ${FORBIDDEN_PHRASE_EXAMPLES}).
Pronouns: address the recipients as "you," "you both," or "you two"; avoid gendered pronouns and honorifics; avoid third-person references—if necessary, use they/them/their; do not assume a shared last name or future roles.`;

export const generateGiftWrapPrompt = ({
  partners,
  context = 'wedding',
  from,
  giftName,
  language = 'en-US',
  country,
}: {
  partners: string;
  context?: string;
  from: string;
  giftName: string;
  language?: string;
  country?: string;
}) =>
  [
    `Write a short, first-person ${context} gift note in the language of the locale "${language}" from ${from} to ${partners}.`,
    'Tone: warm and friendly.',
    buildCultureLine(country, `${context} gift note`),
    `Content: reference their ${giftName} (no mention of any relationship between the giver and recipients).`,
    INCLUSIVE_LANGUAGE_LINES,
    `Names: use names exactly as provided and in this order: ${partners}.`,
    'Length: 2–4 sentences.',
    'Prohibitions: no religious references, no emojis, no hashtags, no quotes, no sign-off or signature line.',
    'Output: the note text only, with no headings or explanations.',
  ]
    .filter(Boolean)
    .join('\n');

export const generateCongratulationsPrompt = ({
  partners,
  from,
  isPlural,
  rsvp,
  language = 'en-US',
  country,
}: {
  partners: string;
  from: string;
  isPlural?: boolean;
  rsvp?: string;
  language?: string;
  country?: string;
}) => {
  const attendance = rsvp === 'all' ? ' Say that you are looking forward to attending the wedding.' : rsvp === 'none' ? ' Say that you are sorry to miss the wedding.' : '';

  return [
    `Simple first-person${isPlural ? ' plural' : ''} message starting with a salutation and congratulating ${partners} on their upcoming wedding, written in the language of the locale "${language}".${attendance} Message should finish with a signoff from ${from}.`,
    buildCultureLine(country, 'wedding congratulations message'),
    INCLUSIVE_LANGUAGE_LINES,
  ]
    .filter(Boolean)
    .join('\n');
};
